import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Globe,
  History,
  Minus,
  Monitor,
  Plus,
  RotateCcw,
  Shield,
  ShieldOff,
  ShoppingBag,
  Smartphone,
  Trash2,
  User,
  Zap,
} from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatDate, formatDateTime, timeAgo } from '@/lib/utils'
import { useUsersStore } from '@/stores/users-store'

type Tab = 'profile' | 'credits' | 'purchases' | 'activity' | 'sessions'

const TABS: Array<{ key: Tab; label: string; icon: typeof User }> = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'credits', label: 'Credits', icon: CreditCard },
  { key: 'purchases', label: 'Purchases', icon: ShoppingBag },
  { key: 'activity', label: 'Activity', icon: History },
  { key: 'sessions', label: 'Sessions', icon: Monitor },
]

const ACTION_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  fashion_identify: { label: 'Fashion ID', color: 'text-accent' },
  tryon_generation: { label: 'Try-On', color: 'text-positive' },
  photo_enhancement: { label: 'Enhancement', color: 'text-warning' },
  admin_adjustment: { label: 'Admin Adjust', color: 'text-[#7C6BBF]' },
}

function parseUserAgent(ua: string | null): string {
  if (!ua) return 'Unknown'
  if (ua.includes('iPhone') || ua.includes('iOS')) return 'iOS App'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Safari')) return 'Safari'
  if (ua.includes('Edge')) return 'Edge'
  return 'Other'
}

function DeviceIcon({ ua }: { ua: string | null }) {
  const parsed = parseUserAgent(ua)
  if (parsed === 'iOS App' || parsed === 'Android') {
    return <Smartphone className="h-4 w-4 text-ink-faint" strokeWidth={1.5} />
  }
  return <Globe className="h-4 w-4 text-ink-faint" strokeWidth={1.5} />
}

export function UserDetail() {
  useAuth()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('profile')

  const {
    selectedUser: user,
    isLoadingDetail,
    activity,
    activityPagination,
    isLoadingActivity,
    userSessions,
    sessionsPagination,
    isLoadingSessions,
    purchases,
    purchasesPagination,
    isLoadingPurchases,
    fetchUserDetail,
    fetchActivity,
    fetchSessions,
    fetchPurchases,
    adjustCredits,
    verifyEmail,
    unverifyEmail,
    banUser,
    unbanUser,
    revokeSession,
    refundPurchase,
    clearDetail,
  } = useUsersStore()

  useEffect(() => {
    if (id) {
      fetchUserDetail(id)
    }
    return () => clearDetail()
  }, [id, fetchUserDetail, clearDetail])

  useEffect(() => {
    if (!id) return
    if (activeTab === 'activity') fetchActivity(id)
    if (activeTab === 'sessions') fetchSessions(id)
    if (activeTab === 'purchases') fetchPurchases(id)
  }, [activeTab, id, fetchActivity, fetchSessions, fetchPurchases])

  if (isLoadingDetail || !user) {
    return (
      <AppShell title="User Detail">
        <div className="flex items-center justify-center py-24">
          <p className="font-mono text-xs text-ink-faint animate-pulse">Loading user...</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell title="User Detail">
      <div className="space-y-6">
        {/* Header */}
        <UserHeader
          user={user}
          onBack={() => navigate('/users')}
          onVerifyEmail={verifyEmail}
          onUnverifyEmail={unverifyEmail}
          onBan={banUser}
          onUnban={unbanUser}
        />

        {/* Tab navigation */}
        <div className="flex rounded-md border border-dashed border-border bg-surface">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                activeTab === tab.key
                  ? 'bg-accent/10 text-accent'
                  : 'text-ink-muted hover:bg-surface-alt'
              )}
            >
              <tab.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'profile' && <ProfileTab user={user} />}
        {activeTab === 'credits' && (
          <CreditsTab
            user={user}
            onAdjust={(amount, reason) => adjustCredits(user.id, amount, reason)}
          />
        )}
        {activeTab === 'purchases' && (
          <PurchasesTab
            purchases={purchases}
            pagination={purchasesPagination}
            isLoading={isLoadingPurchases}
            onRefund={(purchaseId, reason) => refundPurchase(user.id, purchaseId, reason)}
            onPageChange={(page) => fetchPurchases(user.id, page)}
          />
        )}
        {activeTab === 'activity' && (
          <ActivityTab
            entries={activity}
            pagination={activityPagination}
            isLoading={isLoadingActivity}
            onPageChange={(page) => fetchActivity(user.id, page)}
          />
        )}
        {activeTab === 'sessions' && (
          <SessionsTab
            sessions={userSessions}
            pagination={sessionsPagination}
            isLoading={isLoadingSessions}
            onRevoke={(sessionId) => revokeSession(user.id, sessionId)}
            onPageChange={(page) => fetchSessions(user.id, page)}
          />
        )}
      </div>
    </AppShell>
  )
}

/* ─── Header ─────────────────────────────────────────────────────── */

interface UserHeaderProps {
  user: {
    id: string
    name: string
    email: string
    emailVerified: boolean | null
    avatarUrl: string | null
    role: string | null
    banned: boolean | null
    banReason: string | null
  }
  onBack: () => void
  onVerifyEmail: (id: string) => Promise<void>
  onUnverifyEmail: (id: string) => Promise<void>
  onBan: (id: string, reason?: string) => Promise<void>
  onUnban: (id: string) => Promise<void>
}

function UserHeader({
  user,
  onBack,
  onVerifyEmail,
  onUnverifyEmail,
  onBan,
  onUnban,
}: UserHeaderProps) {
  const [showBanConfirm, setShowBanConfirm] = useState(false)
  const [banReason, setBanReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const handleBan = async () => {
    setActionLoading(true)
    await onBan(user.id, banReason || undefined)
    setShowBanConfirm(false)
    setBanReason('')
    setActionLoading(false)
  }

  const handleUnban = async () => {
    setActionLoading(true)
    await onUnban(user.id)
    setActionLoading(false)
  }

  const handleEmailVerificationToggle = async () => {
    setActionLoading(true)

    if (user.emailVerified) {
      await onUnverifyEmail(user.id)
    } else {
      await onVerifyEmail(user.id)
    }

    setActionLoading(false)
  }

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-dashed border-border bg-surface p-2 text-ink-muted transition-colors hover:bg-surface-alt"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 font-mono text-sm font-medium text-accent">
          {(user.name?.[0] ?? user.email[0]).toUpperCase()}
        </div>

        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-mono text-sm font-medium text-ink">{user.name}</h2>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] font-medium',
                user.role === 'admin'
                  ? 'bg-accent/10 text-accent border-accent/20'
                  : 'bg-ink-faint/10 text-ink-faint border-ink-faint/20'
              )}
            >
              {user.role === 'admin' && <Shield className="h-2.5 w-2.5" strokeWidth={2} />}
              {user.role ?? 'user'}
            </span>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] font-medium',
                user.emailVerified
                  ? 'border-positive/20 bg-positive/10 text-positive'
                  : 'border-warning/20 bg-warning/10 text-warning'
              )}
            >
              {user.emailVerified ? 'verified' : 'unverified'}
            </span>
            {user.banned && (
              <span className="inline-flex items-center gap-1 rounded-full border border-negative/20 bg-negative/10 px-2 py-0.5 font-mono text-[10px] font-medium text-negative">
                <Ban className="h-2.5 w-2.5" strokeWidth={2} />
                banned
              </span>
            )}
          </div>
          <p className="font-mono text-xs text-ink-faint">{user.email}</p>
          {user.banned && user.banReason && (
            <p className="mt-0.5 font-mono text-[10px] text-negative/70">
              Reason: {user.banReason}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleEmailVerificationToggle}
          disabled={actionLoading}
          className={cn(
            'rounded-md border border-dashed px-3 py-2 font-mono text-[11px] transition-colors disabled:opacity-50',
            user.emailVerified
              ? 'border-warning/30 bg-warning/5 text-warning hover:bg-warning/10'
              : 'border-positive/30 bg-positive/5 text-positive hover:bg-positive/10'
          )}
        >
          {user.emailVerified ? 'Unverify Email' : 'Verify Email'}
        </button>

        {user.banned ? (
          <button
            type="button"
            onClick={handleUnban}
            disabled={actionLoading}
            className="flex items-center gap-1.5 rounded-md border border-dashed border-positive/30 bg-positive/5 px-3 py-2 font-mono text-[11px] text-positive transition-colors hover:bg-positive/10 disabled:opacity-50"
          >
            <ShieldOff className="h-3.5 w-3.5" />
            Unban
          </button>
        ) : showBanConfirm ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Ban reason (optional)"
              className="w-48 rounded-md border border-dashed border-border bg-canvas px-2.5 py-1.5 font-mono text-[11px] text-ink placeholder:text-ink-faint"
            />
            <button
              type="button"
              onClick={handleBan}
              disabled={actionLoading}
              className="rounded-md bg-negative/10 px-3 py-1.5 font-mono text-[11px] text-negative hover:bg-negative/20 disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setShowBanConfirm(false)}
              className="font-mono text-[11px] text-ink-faint hover:text-ink-muted"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowBanConfirm(true)}
            className="flex items-center gap-1.5 rounded-md border border-dashed border-negative/30 bg-negative/5 px-3 py-2 font-mono text-[11px] text-negative transition-colors hover:bg-negative/10"
          >
            <Ban className="h-3.5 w-3.5" />
            Ban User
          </button>
        )}
      </div>
    </div>
  )
}

/* ─── Profile Tab ────────────────────────────────────────────────── */

interface ProfileUser {
  emailVerified: boolean | null
  firstName: string | null
  lastName: string | null
  nickname: string | null
  phone: string | null
  location: string | null
  website: string | null
  bio: string | null
  twoFactorEnabled: boolean | null
  createdAt: string | null
  lastActiveAt: string | null
}

function ProfileTab({ user }: { user: ProfileUser }) {
  const fields = [
    { label: 'Email Verified', value: user.emailVerified ? 'Yes' : 'No' },
    { label: 'First Name', value: user.firstName },
    { label: 'Last Name', value: user.lastName },
    { label: 'Nickname', value: user.nickname },
    { label: 'Phone', value: user.phone },
    { label: 'Location', value: user.location },
    { label: 'Website', value: user.website },
    { label: 'Bio', value: user.bio },
    { label: '2FA Enabled', value: user.twoFactorEnabled ? 'Yes' : 'No' },
    { label: 'Created', value: user.createdAt ? formatDateTime(user.createdAt) : '—' },
    { label: 'Last Active', value: user.lastActiveAt ? timeAgo(user.lastActiveAt) : '—' },
  ]

  return (
    <div className="rounded-lg border border-dashed border-border bg-surface p-6">
      <p className="mb-5 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
        Profile Information
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.label}>
            <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
              {f.label}
            </p>
            <p className="mt-1 font-mono text-xs text-ink">{(f.value as string) || '—'}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Credits Tab ────────────────────────────────────────────────── */

function CreditsTab({
  user,
  onAdjust,
}: {
  user: {
    availableCredits: number | null
    totalCreditsEarned: number | null
    totalCreditsUsed: number | null
  }
  onAdjust: (amount: number, reason: string) => Promise<void>
}) {
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const num = Number(amount)
    if (!num || num === 0) {
      setError('Enter a non-zero amount')
      return
    }
    if (!reason.trim()) {
      setError('Please provide a reason')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      await onAdjust(num, reason.trim())
      setAmount('')
      setReason('')
    } catch (err) {
      setError((err as Error).message)
    }
    setSubmitting(false)
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: 'Available', value: user.availableCredits ?? 0, color: 'text-positive' },
          { label: 'Total Earned', value: user.totalCreditsEarned ?? 0, color: 'text-accent' },
          { label: 'Total Used', value: user.totalCreditsUsed ?? 0, color: 'text-ink-muted' },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-lg border border-dashed border-border bg-surface px-5 py-4"
          >
            <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
              {card.label}
            </p>
            <p className={cn('mt-1 font-mono text-2xl font-semibold tabular-nums', card.color)}>
              {card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Adjustment form */}
      <div className="rounded-lg border border-dashed border-border bg-surface p-6">
        <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
          Adjust Credits
        </p>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
          <div>
            <label
              htmlFor="credit-amount"
              className="block font-mono text-[10px] tracking-wider text-ink-faint uppercase"
            >
              Amount
            </label>
            <div className="mt-1.5 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setAmount(String(Math.max((Number(amount) || 0) - 1, -999)))}
                className="rounded-md border border-dashed border-border bg-canvas p-1.5 text-ink-muted hover:bg-surface-alt"
              >
                <Minus className="h-3 w-3" />
              </button>
              <input
                id="credit-amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="w-24 rounded-md border border-dashed border-border bg-canvas px-3 py-2 text-center font-mono text-xs tabular-nums text-ink placeholder:text-ink-faint"
              />
              <button
                type="button"
                onClick={() => setAmount(String(Math.min((Number(amount) || 0) + 1, 999)))}
                className="rounded-md border border-dashed border-border bg-canvas p-1.5 text-ink-muted hover:bg-surface-alt"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
          <div className="flex-1">
            <label
              htmlFor="credit-reason"
              className="block font-mono text-[10px] tracking-wider text-ink-faint uppercase"
            >
              Reason
            </label>
            <input
              id="credit-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Compensation for bug, manual top-up"
              className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-accent px-4 py-2 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
          >
            {submitting ? 'Saving...' : 'Apply'}
          </button>
        </form>
        {error && <p className="mt-3 font-mono text-[11px] text-negative">{error}</p>}
      </div>
    </div>
  )
}

/* ─── Activity Tab ───────────────────────────────────────────────── */

interface ActivityTabProps {
  entries: Array<{
    type: 'credit_usage' | 'tryon_request'
    timestamp: string
    data: Record<string, unknown>
  }>
  pagination: { page: number; totalPages: number; total: number }
  isLoading: boolean
  onPageChange: (page: number) => void
}

function ActivityTab({ entries, pagination, isLoading, onPageChange }: ActivityTabProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="font-mono text-xs text-ink-faint animate-pulse">Loading activity...</p>
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-surface py-16">
        <p className="font-mono text-xs text-ink-faint">No activity yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {entries.map((entry) => (
          <ActivityItem
            key={`${entry.type}-${entry.timestamp}-${String(entry.data.id)}`}
            entry={entry}
          />
        ))}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] text-ink-faint">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} entries)
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
              className="rounded-md border border-dashed border-border bg-surface p-1.5 text-ink-muted transition-colors hover:bg-surface-alt disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPageChange(pagination.page + 1)}
              className="rounded-md border border-dashed border-border bg-surface p-1.5 text-ink-muted transition-colors hover:bg-surface-alt disabled:opacity-40"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ActivityItem({
  entry,
}: {
  entry: { type: string; timestamp: string; data: Record<string, unknown> }
}) {
  const isCreditUsage = entry.type === 'credit_usage'
  const actionType = (entry.data.actionType as string) ?? ''
  const meta = ACTION_TYPE_LABELS[actionType]

  return (
    <div className="flex items-start gap-3 rounded-lg border border-dashed border-border bg-surface px-4 py-3">
      <div
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          isCreditUsage ? 'bg-accent/10' : 'bg-positive/10'
        )}
      >
        {isCreditUsage ? (
          <Zap className="h-3.5 w-3.5 text-accent" strokeWidth={1.5} />
        ) : (
          <Clock className="h-3.5 w-3.5 text-positive" strokeWidth={1.5} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isCreditUsage ? (
            <>
              <span className={cn('font-mono text-xs font-medium', meta?.color ?? 'text-ink')}>
                {meta?.label ?? actionType}
              </span>
              <span className="font-mono text-xs tabular-nums text-ink-muted">
                {(entry.data.creditsUsed as number) > 0 ? '-' : '+'}
                {Math.abs(entry.data.creditsUsed as number)} credits
              </span>
              {entry.data.balanceAfter != null && (
                <span className="font-mono text-[10px] text-ink-faint">
                  (bal: {entry.data.balanceAfter as number})
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-mono text-xs font-medium text-positive">Try-On Request</span>
              <span
                className={cn(
                  'rounded-full border px-2 py-0.5 font-mono text-[10px]',
                  entry.data.status === 'completed'
                    ? 'border-positive/20 bg-positive/10 text-positive'
                    : 'border-negative/20 bg-negative/10 text-negative'
                )}
              >
                {entry.data.status as string}
              </span>
              {entry.data.generationTimeMs && (
                <span className="font-mono text-[10px] text-ink-faint">
                  {((entry.data.generationTimeMs as number) / 1000).toFixed(1)}s
                </span>
              )}
            </>
          )}
        </div>

        {(() => {
          if (!isCreditUsage || !entry.data.metadata || typeof entry.data.metadata !== 'object')
            return null
          const reason = (entry.data.metadata as Record<string, unknown>).reason
          if (!reason) return null
          return <p className="mt-0.5 font-mono text-[10px] text-ink-faint">{String(reason)}</p>
        })()}
      </div>

      <p
        className="shrink-0 font-mono text-[10px] text-ink-faint"
        title={formatDateTime(entry.timestamp)}
      >
        {timeAgo(entry.timestamp)}
      </p>
    </div>
  )
}

/* ─── Purchases Tab ─────────────────────────────────────────────── */

interface PurchasesTabProps {
  purchases: Array<{
    id: string
    transactionId: string
    productId: string
    productLabel: string
    creditsGranted: number
    purchaseDate: string
    environment: string
    verifiedWithApple: boolean
    refundedAt: string | null
    refundReason: string | null
    createdAt: string
  }>
  pagination: { page: number; totalPages: number; total: number }
  isLoading: boolean
  onRefund: (purchaseId: string, reason: string) => Promise<void>
  onPageChange: (page: number) => void
}

function PurchasesTab({
  purchases,
  pagination,
  isLoading,
  onRefund,
  onPageChange,
}: PurchasesTabProps) {
  const [refundTarget, setRefundTarget] = useState<string | null>(null)
  const [refundReason, setRefundReason] = useState('')
  const [refunding, setRefunding] = useState(false)
  const [refundError, setRefundError] = useState<string | null>(null)

  const handleRefund = async (purchaseId: string) => {
    if (!refundReason.trim()) return
    setRefunding(true)
    setRefundError(null)
    try {
      await onRefund(purchaseId, refundReason.trim())
      setRefundTarget(null)
      setRefundReason('')
    } catch (err) {
      setRefundError((err as Error).message || 'Refund failed')
    }
    setRefunding(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="font-mono text-xs text-ink-faint animate-pulse">Loading purchases...</p>
      </div>
    )
  }

  if (purchases.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-surface py-16">
        <p className="font-mono text-xs text-ink-faint">No purchases yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-dashed border-border bg-surface">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dashed border-border">
              {['Product', 'Credits', 'Environment', 'Verified', 'Date', 'Status', 'Actions'].map(
                (h) => (
                  <th
                    key={h}
                    className={cn(
                      'px-4 py-3 font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase',
                      h === 'Actions' ? 'text-right' : 'text-left'
                    )}
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {purchases.map((purchase) => {
              const isRefunded = !!purchase.refundedAt
              return (
                <tr
                  key={purchase.id}
                  className="border-b border-dashed border-border-muted last:border-0"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-mono text-xs text-ink">{purchase.productLabel}</p>
                      <p className="font-mono text-[10px] text-ink-faint">
                        {purchase.transactionId}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-medium tabular-nums text-positive">
                    +{purchase.creditsGranted}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-block rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
                        purchase.environment === 'production'
                          ? 'border-positive/20 bg-positive/10 text-positive'
                          : 'border-warning/20 bg-warning/10 text-warning'
                      )}
                    >
                      {purchase.environment}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-ink-faint">
                    {purchase.verifiedWithApple ? 'Apple' : 'Local'}
                  </td>
                  <td
                    className="px-4 py-3 font-mono text-[11px] text-ink-faint"
                    title={formatDateTime(purchase.purchaseDate)}
                  >
                    {timeAgo(purchase.purchaseDate)}
                  </td>
                  <td className="px-4 py-3">
                    {isRefunded ? (
                      <div>
                        <span className="inline-block rounded-full border border-negative/20 bg-negative/10 px-2.5 py-0.5 font-mono text-[10px] font-medium text-negative">
                          refunded
                        </span>
                        {purchase.refundReason && (
                          <p className="mt-0.5 font-mono text-[10px] text-ink-faint">
                            {purchase.refundReason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="inline-block rounded-full border border-positive/20 bg-positive/10 px-2.5 py-0.5 font-mono text-[10px] font-medium text-positive">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      {isRefunded ? (
                        <span className="font-mono text-[10px] text-ink-faint">—</span>
                      ) : refundTarget === purchase.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            placeholder="Refund reason"
                            className="w-40 rounded-md border border-dashed border-border bg-canvas px-2.5 py-1.5 font-mono text-[11px] text-ink placeholder:text-ink-faint"
                          />
                          <button
                            type="button"
                            onClick={() => handleRefund(purchase.id)}
                            disabled={refunding || !refundReason.trim()}
                            className="rounded bg-negative/10 px-2 py-0.5 font-mono text-[10px] text-negative hover:bg-negative/20 disabled:opacity-50"
                          >
                            {refunding ? '...' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRefundTarget(null)
                              setRefundReason('')
                            }}
                            className="font-mono text-[10px] text-ink-faint hover:text-ink-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setRefundTarget(purchase.id)}
                          className="flex items-center gap-1 rounded p-1 text-ink-faint transition-colors hover:bg-negative/10 hover:text-negative"
                          title="Refund purchase"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {refundError && (
        <p className="font-mono text-[11px] text-negative">{refundError}</p>
      )}

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] text-ink-faint">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} purchases)
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
              className="rounded-md border border-dashed border-border bg-surface p-1.5 text-ink-muted transition-colors hover:bg-surface-alt disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPageChange(pagination.page + 1)}
              className="rounded-md border border-dashed border-border bg-surface p-1.5 text-ink-muted transition-colors hover:bg-surface-alt disabled:opacity-40"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Sessions Tab ───────────────────────────────────────────────── */

interface SessionsTabProps {
  sessions: Array<{
    id: string
    ipAddress: string | null
    userAgent: string | null
    createdAt: string | null
    expiresAt: string
  }>
  pagination: { page: number; totalPages: number; total: number }
  isLoading: boolean
  onRevoke: (sessionId: string) => void
  onPageChange: (page: number) => void
}

function SessionsTab({
  sessions,
  pagination,
  isLoading,
  onRevoke,
  onPageChange,
}: SessionsTabProps) {
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="font-mono text-xs text-ink-faint animate-pulse">Loading sessions...</p>
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-surface py-16">
        <p className="font-mono text-xs text-ink-faint">No sessions</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-dashed border-border bg-surface">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dashed border-border">
              <th className="px-4 py-3 text-left font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase">
                Device
              </th>
              <th className="px-4 py-3 text-left font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase">
                IP Address
              </th>
              <th className="px-4 py-3 text-left font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase">
                Created
              </th>
              <th className="px-4 py-3 text-left font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase">
                Expires
              </th>
              <th className="px-4 py-3 text-left font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-right font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => {
              const isExpired = new Date(session.expiresAt) < new Date()
              return (
                <tr
                  key={session.id}
                  className="border-b border-dashed border-border-muted last:border-0"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <DeviceIcon ua={session.userAgent} />
                      <span className="font-mono text-xs text-ink">
                        {parseUserAgent(session.userAgent)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-ink-muted">
                    {session.ipAddress ?? '—'}
                  </td>
                  <td
                    className="px-4 py-3 font-mono text-[11px] text-ink-faint"
                    title={session.createdAt ? formatDateTime(session.createdAt) : undefined}
                  >
                    {session.createdAt ? timeAgo(session.createdAt) : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-ink-faint">
                    {formatDate(session.expiresAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-block rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
                        isExpired
                          ? 'border-ink-faint/20 bg-ink-faint/10 text-ink-faint'
                          : 'border-positive/20 bg-positive/10 text-positive'
                      )}
                    >
                      {isExpired ? 'expired' : 'active'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      {confirmRevoke === session.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              onRevoke(session.id)
                              setConfirmRevoke(null)
                            }}
                            className="rounded bg-negative/10 px-2 py-0.5 font-mono text-[10px] text-negative hover:bg-negative/20"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRevoke(null)}
                            className="font-mono text-[10px] text-ink-faint hover:text-ink-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRevoke(session.id)}
                          className="rounded p-1 text-ink-faint transition-colors hover:bg-negative/10 hover:text-negative"
                          title="Revoke session"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] text-ink-faint">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} sessions)
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
              className="rounded-md border border-dashed border-border bg-surface p-1.5 text-ink-muted transition-colors hover:bg-surface-alt disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPageChange(pagination.page + 1)}
              className="rounded-md border border-dashed border-border bg-surface p-1.5 text-ink-muted transition-colors hover:bg-surface-alt disabled:opacity-40"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
